import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Heart, User, LogOut, Package, Wallet, LayoutDashboard, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { NavSearch } from "@/components/NavSearch";
import { ReelsModal } from "@/components/ReelsModal";

const links = [
  { label: "Home", to: "/" },
  { label: "Shop All", to: "/shop" },
  { label: "Why ViRa", to: "/#manifesto" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const navigate = useNavigate();
  const { count, setIsOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const [announcement, setAnnouncement] = useState("");
  const [brands, setBrands] = useState([]);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catsOpen, setCatsOpen] = useState(false);
  const [featuredBanner, setFeaturedBanner] = useState(null);
  const [reels, setReels] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setAnnouncement(r.data.announcement || "");
      const banners = r.data.banners || [];
      setFeaturedBanner(banners.find((b) => b.image && b.title === "The ViRa Sale") || banners.find((b) => b.image) || null);
      setReels(r.data.instagram_reels || []);
    }).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        document.getElementById(to.slice(2))?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      navigate(to);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      {announcement && (
        <div className="bg-gold text-white text-center text-[10px] uppercase tracking-[0.3em] py-2 px-4" data-testid="announcement-bar">
          {announcement}
        </div>
      )}
      <div className="bg-white/80 backdrop-blur-2xl border-b border-zinc-200">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("/")} data-testid="nav-logo" aria-label="ViRa home">
          <Logo size="md" />
        </button>
        <nav className="hidden md:flex items-center gap-10">
          {links.slice(0, 2).map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <div className="relative">
            <button
              onClick={() => setBrandsOpen(!brandsOpen)}
              data-testid="nav-brands-btn"
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300"
            >
              Brands
              <ChevronDown size={12} className={`transition-transform duration-300 ${brandsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {brandsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-9 w-60 bg-white border border-zinc-200 shadow-xl shadow-pink-100/40 py-2 z-50"
                  data-testid="brands-menu"
                >
                  {brands.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => { setBrandsOpen(false); navigate(`/shop?brand=${b.slug}`); }}
                      data-testid={`nav-brand-${b.slug}`}
                      className="w-full flex items-center justify-between px-5 py-3 text-sm text-zinc-600 hover:text-gold hover:bg-pink-50 transition-colors duration-200"
                    >
                      <span>{b.name}</span>
                      <span className="text-[10px] text-zinc-400">{b.product_count ?? 0}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => { setBrandsOpen(false); navigate("/shop"); }}
                    className="w-full text-left px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-gold hover:bg-pink-50 transition-colors duration-200 border-t border-zinc-100 mt-1"
                  >
                    View All Brands
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="relative">
            <button
              onClick={() => setCatsOpen(!catsOpen)}
              data-testid="nav-categories-btn"
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300"
            >
              Categories
              <ChevronDown size={12} className={`transition-transform duration-300 ${catsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {catsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-9 w-[880px] max-w-[calc(100vw-2rem)] bg-white border border-zinc-200 shadow-xl shadow-pink-100/40 p-6 z-50 grid grid-cols-3 gap-x-8 gap-y-5"
                  data-testid="categories-menu"
                >
                  <div className="col-span-2 grid grid-cols-2 gap-x-8 gap-y-5">
                  {categories.map((c) => (
                    <div key={c.slug}>
                      <button
                        onClick={() => { setCatsOpen(false); navigate(`/shop?cat=${c.slug}`); }}
                        data-testid={`nav-cat-${c.slug}`}
                        className="text-[10px] uppercase tracking-[0.25em] text-gold hover:text-champagne transition-colors duration-200"
                      >
                        {c.name} <span className="text-zinc-300 normal-case tracking-normal">({c.count})</span>
                      </button>
                      <div className="mt-2 space-y-1">
                        {(c.subs || []).map((s) => (
                          <button
                            key={s.slug}
                            onClick={() => { setCatsOpen(false); navigate(`/shop?cat=${c.slug}&sub=${s.slug}`); }}
                            data-testid={`nav-sub-${s.slug}`}
                            className="block w-full text-left text-sm text-zinc-600 hover:text-gold transition-colors duration-200 py-0.5"
                          >
                            {s.name} <span className="text-[10px] text-zinc-400">({s.count})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  </div>
                  {featuredBanner && (
                    <button
                      onClick={() => { setCatsOpen(false); navigate(featuredBanner.cta || "/offers"); }}
                      data-testid="categories-featured-tile"
                      className="relative overflow-hidden text-left min-h-[240px] group"
                    >
                      <img src={featuredBanner.image} alt={featuredBanner.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      <div className="absolute bottom-0 p-4">
                        <p className="text-[9px] uppercase tracking-[0.3em] text-white/70">Limited Time</p>
                        <p className="text-white font-medium text-lg leading-tight mt-1">{featuredBanner.title}</p>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mt-2">Shop Now →</p>
                      </div>
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {links.slice(2).map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <NavSearch className="hidden lg:block w-56 xl:w-72" />
          <ReelsModal reels={reels} />
          <button
            onClick={() => navigate("/wishlist")}
            data-testid="nav-wishlist-btn"
            className="relative text-zinc-600 hover:text-gold transition-colors duration-300"
            aria-label="Wishlist"
          >
            <Heart size={18} />
            {wishCount > 0 && (
              <span className="absolute -top-2 -right-2.5 bg-gold text-white text-[9px] font-semibold w-4 h-4 flex items-center justify-center rounded-full" data-testid="nav-wishlist-count">
                {wishCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsOpen(true)}
            data-testid="nav-cart-btn"
            className="relative text-zinc-600 hover:text-gold transition-colors duration-300"
            aria-label="Open bag"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2.5 bg-gold text-white text-[9px] font-semibold w-4 h-4 flex items-center justify-center rounded-full" data-testid="nav-cart-count">
                {count}
              </span>
            )}
          </button>
          <div className="relative">
            <button
              onClick={() => (user ? setAccountOpen(!accountOpen) : navigate("/login"))}
              data-testid="nav-account-btn"
              className="flex items-center gap-2 text-zinc-600 hover:text-gold transition-colors duration-300"
              aria-label="Account"
            >
              <User size={18} />
              {user && <span className="text-xs max-w-[80px] truncate">{user.name?.split(" ")[0]}</span>}
            </button>
            <AnimatePresence>
              {accountOpen && user && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-10 w-52 bg-white border border-zinc-200 shadow-xl shadow-pink-100/40 py-2 z-50"
                  data-testid="account-menu"
                >
                  {user.role === "admin" && (
                    <button onClick={() => { setAccountOpen(false); navigate("/admin/dashboard"); }} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-zinc-600 hover:text-gold hover:bg-pink-50 transition-colors duration-200" data-testid="account-menu-admin">
                      <LayoutDashboard size={14} /> Back Office
                    </button>
                  )}
                  <button onClick={() => { setAccountOpen(false); navigate("/account"); }} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-zinc-600 hover:text-gold hover:bg-pink-50 transition-colors duration-200" data-testid="account-menu-wallet">
                    <Wallet size={14} /> Wallet & Account
                  </button>
                  <button onClick={() => { setAccountOpen(false); navigate("/orders"); }} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-zinc-600 hover:text-gold hover:bg-pink-50 transition-colors duration-200" data-testid="account-menu-orders">
                    <Package size={14} /> My Orders
                  </button>
                  <button onClick={() => { setAccountOpen(false); navigate("/wishlist"); }} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-zinc-600 hover:text-gold hover:bg-pink-50 transition-colors duration-200" data-testid="account-menu-wishlist">
                    <Heart size={14} /> Wishlist
                  </button>
                  <button onClick={async () => { setAccountOpen(false); await logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-5 py-3 text-sm text-zinc-600 hover:text-red-500 hover:bg-red-50 transition-colors duration-200" data-testid="account-menu-logout">
                    <LogOut size={14} /> Sign Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>
        <div className="flex items-center gap-5 md:hidden">
          <ReelsModal reels={reels} />
          <button onClick={() => setIsOpen(true)} className="relative text-zinc-900" aria-label="Open bag" data-testid="nav-cart-btn-mobile">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2.5 bg-gold text-white text-[9px] font-semibold w-4 h-4 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </button>
          <button className="text-zinc-900" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-y-auto overscroll-contain max-h-[calc(100dvh-7.5rem)] bg-white/95 border-b border-zinc-200"
            data-testid="nav-mobile-menu"
            data-lenis-prevent
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <NavSearch onNavigate={() => setOpen(false)} testid="nav-search-mobile" />
              {links.map((l) => (
                <button
                  key={l.label}
                  onClick={() => go(l.to)}
                  className="text-left text-sm uppercase tracking-[0.3em] text-zinc-600 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 pt-2 border-t border-zinc-100">Brands</p>
              <div className="grid grid-cols-2 gap-3">
                {brands.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => { setOpen(false); navigate(`/shop?brand=${b.slug}`); }}
                    data-testid={`nav-brand-mobile-${b.slug}`}
                    className="text-left text-sm text-zinc-600 hover:text-gold transition-colors duration-300 border border-zinc-100 px-4 py-3"
                  >
                    {b.name}
                  </button>
                ))}
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 pt-2 border-t border-zinc-100">Categories</p>
              {categories.map((c) => (
                <div key={c.slug}>
                  <button
                    onClick={() => { setOpen(false); navigate(`/shop?cat=${c.slug}`); }}
                    data-testid={`nav-cat-mobile-${c.slug}`}
                    className="text-left text-sm font-medium text-zinc-800 hover:text-gold transition-colors duration-300"
                  >
                    {c.name} <span className="text-[10px] text-zinc-400">({c.count})</span>
                  </button>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(c.subs || []).map((s) => (
                      <button
                        key={s.slug}
                        onClick={() => { setOpen(false); navigate(`/shop?cat=${c.slug}&sub=${s.slug}`); }}
                        data-testid={`nav-sub-mobile-${s.slug}`}
                        className="text-xs text-zinc-500 hover:text-gold transition-colors duration-200 border border-zinc-100 px-3 py-1.5"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      </div>
    </header>
  );
};
